import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { track } from '@/api/tracking'
import { LocaleLink } from '@/navigation/LocaleLink'
import { ROUTES } from '@/navigation/constants'
import type { LandingPage } from '@tipi/shared'

import { buildAppOutboundUrl } from './buildAppOutboundUrl'
import {
    ASSETS,
    FOOD_COST_LANDING_PAGE,
    LANDING_DIRECT_CONVERSION_EXPERIMENT,
    OUTBOUND_TRACK_OPTIONS,
} from './constants'

const CLIENT_ACCESS_APP_URL = buildAppOutboundUrl('client_access')

const SUBPAGE_LINKS = [
    { to: ROUTES.learn, labelKey: 'footer.learn', link: 'learn' },
    { to: ROUTES.news, labelKey: 'footer.news', link: 'news' },
    { to: ROUTES.press, labelKey: 'footer.press', link: 'press' },
    { to: ROUTES.faqs, labelKey: 'footer.faqs', link: 'faqs' },
] as const

const NAV_LINK_CLASS =
    'whitespace-nowrap font-semibold text-sm text-[#F4502A] no-underline transition-colors hover:text-[#F4502A]/80'

const MOBILE_MENU_LINK_CLASS =
    'block w-full rounded-xl px-3 py-2.5 font-semibold text-sm text-[#F4502A] no-underline transition-colors hover:bg-[#F4502A]/10'

const MOBILE_MENU_ID = 'food-cost-header-menu'

interface FoodCostHeaderProps {
    onCtaClick: () => void
    progress: number
    isDirectConversion?: boolean
    signupUrl?: string
    landingPage?: LandingPage
    ctaLabel?: string
    mixpanelContext?: Record<string, unknown>
}

function trackHeaderLinkClick(link: string, landingPage: LandingPage, mixpanelContext?: Record<string, unknown>) {
    const isClientAccess = link === 'client_access'
    track(
        'Header Link Clicked',
        {
            landing_page: landingPage,
            link,
            ...mixpanelContext,
            ...(isClientAccess ? { cta: 'client_access', destination_url: CLIENT_ACCESS_APP_URL } : {}),
        },
        isClientAccess ? OUTBOUND_TRACK_OPTIONS : undefined,
    )
}

export function FoodCostHeader({
    onCtaClick,
    progress,
    isDirectConversion = false,
    signupUrl,
    landingPage = FOOD_COST_LANDING_PAGE,
    ctaLabel,
    mixpanelContext,
}: FoodCostHeaderProps) {
    const { t } = useTranslation()
    const [menuOpen, setMenuOpen] = useState(false)
    const headerShellRef = useRef<HTMLDivElement>(null)
    const translateY = `${(1 - progress) * -120}%`

    const headerCtaLabel = isDirectConversion
        ? t('foodCost.header.ctaDirect', 'Empieza hoy sin coste')
        : (ctaLabel ?? t('foodCost.header.cta', 'Probar ahora'))

    useEffect(() => {
        if (!menuOpen) return

        const closeMenu = () => setMenuOpen(false)

        const handlePointerDown = (event: PointerEvent) => {
            if (headerShellRef.current?.contains(event.target as Node)) return
            closeMenu()
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') closeMenu()
        }

        document.addEventListener('pointerdown', handlePointerDown)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [menuOpen])

    const handleCtaClick = () => {
        setMenuOpen(false)
        track(
            'Header CTA Clicked',
            {
                landing_page: landingPage,
                ...mixpanelContext,
                ...(isDirectConversion
                    ? {
                          experiment: LANDING_DIRECT_CONVERSION_EXPERIMENT,
                          cta: 'header_cta',
                          destination_url: signupUrl,
                      }
                    : {}),
            },
            isDirectConversion ? OUTBOUND_TRACK_OPTIONS : undefined,
        )
        if (!isDirectConversion) onCtaClick()
    }

    const handleNavLinkClick = (link: string) => {
        setMenuOpen(false)
        trackHeaderLinkClick(link, landingPage, mixpanelContext)
    }

    return (
        <div
            className='fixed inset-x-0 top-3 z-40 px-3 transition-[opacity,transform] duration-300 ease-out md:top-4 md:px-6'
            style={{
                opacity: progress,
                transform: `translateY(${translateY})`,
                pointerEvents: progress > 0.05 ? 'auto' : 'none',
            }}
        >
            <div
                ref={headerShellRef}
                className={`mx-auto w-full max-w-[1180px] bg-[#17130E]/[0.03] shadow-[0_8px_24px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.03)] backdrop-blur-[14px] backdrop-saturate-150 ${
                    menuOpen
                        ? 'rounded-[28px] px-2 pb-2 pt-2.5 md:rounded-full md:px-5 md:pb-2.5'
                        : 'rounded-full px-4 py-2.5 md:px-5'
                }`}
            >
                <div className={`flex items-center gap-3 md:gap-5 ${menuOpen ? 'px-2 md:px-0' : ''}`}>
                    <LocaleLink
                        to={ROUTES.home}
                        aria-label={t('foodCost.header.logoAlt', 'Tipi')}
                        className='shrink-0'
                        onClick={() => handleNavLinkClick('home')}
                    >
                        <img
                            src={ASSETS.headerLogo}
                            alt={t('foodCost.header.logoAlt', 'Tipi')}
                            loading='lazy'
                            decoding='async'
                            className='h-[36px] w-auto md:h-[38px]'
                        />
                    </LocaleLink>

                    <div
                        className='hidden h-7 w-px shrink-0 self-center bg-[#17130E]/25 md:block md:h-8'
                        aria-hidden='true'
                    />
                    <nav
                        aria-label={t('foodCost.header.subpagesNavAriaLabel', 'Secciones')}
                        className='no-scrollbar hidden min-w-0 items-center gap-4 overflow-x-auto md:flex md:gap-[26px]'
                    >
                        {SUBPAGE_LINKS.map(link => (
                            <LocaleLink
                                key={link.to}
                                to={link.to}
                                className={NAV_LINK_CLASS}
                                onClick={() => trackHeaderLinkClick(link.link, landingPage, mixpanelContext)}
                            >
                                {t(link.labelKey)}
                            </LocaleLink>
                        ))}
                    </nav>

                    <div className='min-w-0 flex-1' />

                    <a
                        href={CLIENT_ACCESS_APP_URL}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={`hidden shrink-0 md:inline ${NAV_LINK_CLASS}`}
                        onClick={() => trackHeaderLinkClick('client_access', landingPage, mixpanelContext)}
                    >
                        {t('foodCost.header.clientAccess', 'Acceso clientes')}
                    </a>

                    {isDirectConversion && signupUrl ? (
                        <a
                            href={signupUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            onClick={handleCtaClick}
                            className='whitespace-nowrap rounded-full bg-[#D13D1A] px-5 py-3 text-sm font-bold text-white no-underline shadow-[0_4px_14px_rgba(209,61,26,0.4)] md:px-[22px]'
                        >
                            {headerCtaLabel}
                        </a>
                    ) : (
                        <button
                            type='button'
                            onClick={handleCtaClick}
                            className='whitespace-nowrap rounded-full bg-[#D13D1A] px-5 py-3 text-sm font-bold text-white shadow-[0_4px_14px_rgba(209,61,26,0.4)] md:px-[22px]'
                        >
                            {headerCtaLabel}
                        </button>
                    )}

                    <button
                        type='button'
                        className='flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-[#F4502A] text-[#F4502A] md:hidden'
                        aria-expanded={menuOpen}
                        aria-controls={MOBILE_MENU_ID}
                        aria-label={
                            menuOpen
                                ? t('foodCost.header.menuCloseAriaLabel', 'Cerrar menú')
                                : t('foodCost.header.menuOpenAriaLabel', 'Abrir menú')
                        }
                        onClick={() => setMenuOpen(current => !current)}
                    >
                        <svg
                            width='17'
                            height='17'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2.2'
                            strokeLinecap='round'
                            aria-hidden='true'
                        >
                            {menuOpen ? (
                                <>
                                    <path d='M6 6l12 12' />
                                    <path d='M18 6L6 18' />
                                </>
                            ) : (
                                <>
                                    <path d='M4 7h16' />
                                    <path d='M4 12h16' />
                                    <path d='M4 17h16' />
                                </>
                            )}
                        </svg>
                    </button>
                </div>

                <nav
                    id={MOBILE_MENU_ID}
                    aria-label={t('foodCost.header.subpagesNavAriaLabel', 'Secciones')}
                    className={`flex flex-col gap-0.5 md:hidden ${menuOpen ? '' : 'hidden'}`}
                    inert={!menuOpen}
                >
                    {SUBPAGE_LINKS.map(link => (
                        <LocaleLink
                            key={link.to}
                            to={link.to}
                            className={MOBILE_MENU_LINK_CLASS}
                            onClick={() => handleNavLinkClick(link.link)}
                        >
                            {t(link.labelKey)}
                        </LocaleLink>
                    ))}
                    <a
                        href={CLIENT_ACCESS_APP_URL}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={MOBILE_MENU_LINK_CLASS}
                        onClick={() => handleNavLinkClick('client_access')}
                    >
                        {t('foodCost.header.clientAccess', 'Acceso clientes')}
                    </a>
                </nav>
            </div>
        </div>
    )
}
